import axios from "axios";
import { resolveApiBaseUrl } from "./resolveApiBaseUrl";

const DEFAULT_TIMEOUT = 15000;

const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: DEFAULT_TIMEOUT,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

export const setAuthToken = (token) => {
  if (!token) {
    delete apiClient.defaults.headers.common.Authorization;
    return;
  }

  apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const updateClientBaseUrl = (url) => {
  if (url) {
    apiClient.defaults.baseURL = url;
  }
};

export const getApiBaseUrl = () => apiClient.defaults.baseURL || resolveApiBaseUrl();

apiClient.interceptors.request.use((config) => {
  config.baseURL = resolveApiBaseUrl();
  return config;
});

let refreshPromise = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url || "";

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !url.includes("/auth/email/send-code") &&
      !url.includes("/auth/email/verify-code") &&
      !url.includes("/auth/refresh") &&
      !url.includes("/auth/logout")
    ) {
      originalRequest._retry = true;

      try {
        if (!refreshPromise) {
          refreshPromise = apiClient.post("/auth/refresh").finally(() => {
            refreshPromise = null;
          });
        }

        const { data } = await refreshPromise;
        setAuthToken(data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;

        window.dispatchEvent(
          new CustomEvent("auth:token-refreshed", {
            detail: { accessToken: data.accessToken, user: data.user },
          }),
        );

        return apiClient(originalRequest);
      } catch (refreshError) {
        window.dispatchEvent(new CustomEvent("auth:session-expired"));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export const apiGet = async (url, config = {}) => {
  const response = await apiClient.get(url, config);
  return response.data;
};

export const apiPost = async (url, data = {}, config = {}) => {
  const response = await apiClient.post(url, data, config);
  return response.data;
};

export const apiPut = async (url, data = {}, config = {}) => {
  const response = await apiClient.put(url, data, config);
  return response.data;
};

export const apiPatch = async (url, data = {}, config = {}) => {
  const response = await apiClient.patch(url, data, config);
  return response.data;
};

export const apiDelete = async (url, config = {}) => {
  const response = await apiClient.delete(url, config);
  return response.data;
};

export const apiUpload = async (url, formData, config = {}) => {
  const response = await apiClient.post(url, formData, {
    ...config,
    headers: {
      ...(config.headers || {}),
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export default apiClient;
